import { useForm, Controller } from "react-hook-form";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Mail, User, Briefcase, Activity, ArrowRight, ShieldCheck, Check, ChevronLeft, ChevronRight, FileText, MapPin, Globe, AlertCircle, Building2 } from "lucide-react";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api-client-react";

type RoleType = "member" | "agent" | "provider";

type EmailFormData = { email: string };
type OTPFormData = { otp: string };
type DetailsFormData = {
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  gender: string;
  headline: string;
};

const SALUTATIONS = ["Dr.", "Mr.", "Mrs.", "Ms.", "Prof.", "Rev.", "Other"];
const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];

const WIZARD_STEPS = [
  { label: "Personal", icon: User },
  { label: "Professional", icon: Briefcase },
  { label: "About", icon: FileText },
  { label: "Locations", icon: MapPin },
  { label: "Online & Account", icon: Globe },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {WIZARD_STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                done ? "bg-primary border-primary text-white"
                  : active ? "bg-white border-primary text-primary shadow-md shadow-primary/20"
                    : "bg-white border-border text-muted-foreground"
              )}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={cn(
                "text-[11px] font-medium whitespace-nowrap hidden sm:block",
                active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"
              )}>
                {step.label}
              </span>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={cn(
                "w-10 sm:w-16 h-0.5 mb-5 transition-all duration-500",
                i < current ? "bg-primary" : "bg-border"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

export default function Register() {
  const [, setLocation] = useLocation();
  const [role, setRole] = useState<RoleType>("member");
  // step: 0 = Role/Email, 1 = OTP, 2 = NPI Verification
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [npiInput, setNpiInput] = useState("");
  const [practiceTypeInput, setPracticeTypeInput] = useState<"individual" | "group">("group");
  const [individualNpiInput, setIndividualNpiInput] = useState("");
  const [groupNpiInput, setGroupNpiInput] = useState("");
  const [affiliatedNameInput, setAffiliatedNameInput] = useState("");
  const [npiLoading, setNpiLoading] = useState(false);
  const [npiFeedback, setNpiFeedback] = useState<{ type: "success" | "error" | "info"; msg: string; alreadyConnected?: boolean } | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const emailForm = useForm<EmailFormData>({
    defaultValues: { email: "" },
    mode: "onChange",
  });

  const otpForm = useForm<OTPFormData>({
    defaultValues: { otp: "" },
    mode: "onChange",
  });

  const onEmailSubmit = async (data: EmailFormData) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, type: "register", role }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setStep(1);
      scrollTop();
    } catch (err: any) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleNpiLookup = async (manualNpi?: string) => {
    const targetNpi = (manualNpi !== undefined ? manualNpi : (individualNpiInput || groupNpiInput || npiInput)).trim();

    localStorage.setItem("registerIndividualNpi", individualNpiInput.trim());
    localStorage.setItem("registerGroupNpi", groupNpiInput.trim());
    localStorage.setItem("registerAffiliatedName", affiliatedNameInput.trim());

    if (!targetNpi) {
      localStorage.removeItem("npiProviderDetails");
      localStorage.removeItem("registerNpi");
      setLocation("/provider/register");
      return;
    }

    setNpiLoading(true);
    setNpiFeedback(null);
    try {
      const res = await fetch(`${API_BASE}/auth/npi-lookup/${encodeURIComponent(targetNpi)}`);
      const json = await res.json();

      if (json.alreadyConnected || (!json.success && json.error?.includes("associated with another provider"))) {
        setNpiFeedback({
          type: "error",
          alreadyConnected: true,
          msg: json.error || "This NPI number is associated with another provider account. Please log in with that account."
        });
        return;
      }

      if (json.success && json.found && json.data) {
        const enrichedData = {
          ...json.data,
          individualNpiNumber: individualNpiInput.trim() || json.data.individualNpiNumber || (targetNpi.length === 10 ? targetNpi : ''),
          groupNpiNumber: groupNpiInput.trim() || json.data.groupNpiNumber || '',
          affiliatedName: affiliatedNameInput.trim() || json.data.affiliatedName || '',
          npiNumber: targetNpi
        };
        localStorage.setItem("npiProviderDetails", JSON.stringify(enrichedData));
        localStorage.setItem("registerNpi", targetNpi);
        setNpiFeedback({
          type: "success",
          msg: "Provider details found in Database! Pre-filling your profile..."
        });
        setTimeout(() => {
          setLocation("/provider/register");
        }, 1200);
      } else {
        localStorage.removeItem("npiProviderDetails");
        localStorage.setItem("registerNpi", targetNpi);
        setLocation("/provider/register");
      }
    } catch (err: any) {
      localStorage.removeItem("npiProviderDetails");
      localStorage.setItem("registerNpi", targetNpi);
      setNpiFeedback({
        type: "error",
        msg: "Failed to connect for NPI lookup. You can proceed manually."
      });
      setTimeout(() => {
        setLocation("/provider/register");
      }, 1500);
    } finally {
      setNpiLoading(false);
    }
  };


  const onOTPSubmit = async (data: OTPFormData) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailForm.getValues("email"), otp: data.otp }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);

      // Save email for registration flow
      localStorage.setItem("registerEmail", emailForm.getValues("email"));

      if (role === "provider") {
        setStep(2);
        scrollTop();
      } else if (role === "agent") {
        setLocation("/agent/register");
      } else {
        setLocation("/member/register");
      }
    } catch (err: any) {
      setError(err.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };


  const roles = [
    { id: "member", label: "Member", icon: User, desc: "Manage your health" },
    { id: "agent", label: "Agent", icon: Briefcase, desc: "Support members" },
    { id: "provider", label: "Provider", icon: Activity, desc: "Manage patients" },
  ] as const;

  return (
    <div className="min-h-screen grid lg:grid-cols-2" ref={topRef}>
      <div className="hidden lg:flex flex-col bg-gradient-to-br from-secondary via-secondary/90 to-primary p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl">StraightDoc</span>
        </div>
        <div className="flex-1 flex flex-col justify-center relative z-10">
          <motion.h1 initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="text-4xl font-bold mb-4">
            Join the future of healthcare
          </motion.h1>
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="text-white/70 text-lg mb-8">
            Create your free account to access verified providers, instant cost estimates, and seamless care coordination.
          </motion.p>
          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur border border-white/20">
            <p className="text-sm font-medium mb-4">Why choose StraightDoc?</p>
            {["Passwordless secure access", "Find top-rated professionals", "Real-time cost estimates", "HIPAA-secure environment"].map((item, i) => (
              <motion.div key={item} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-3 text-white/90 mb-3 last:mb-0">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col p-6 sm:p-10 bg-background relative overflow-y-auto">
        <div className="w-full max-w-xl mx-auto relative z-10 my-auto pt-4 pb-12">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">StraightDoc</span>
            </div>
            <Link href="/login" className="text-sm text-primary font-medium hover:underline">Sign in</Link>
          </div>


          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22 }}
            >
              {step === 0 && (
                <div className="space-y-6">
                  <div className="mb-2">
                    <h2 className="text-2xl font-bold mb-2">Create your account</h2>
                    <p className="text-muted-foreground mb-6">
                      Already have an account? <Link href="/login" className="text-primary hover:underline font-medium">Sign in</Link>
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 rounded-xl bg-destructive/10 text-destructive text-sm font-medium border border-destructive/20 flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-destructive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <AlertCircle className="w-3.5 h-3.5" />
                      </div>
                      {error}
                    </div>
                  )}

                  <div>
                    <Label className="mb-3 block text-muted-foreground">I am joining as a:</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {roles.map(r => {
                        const Icon = r.icon;
                        const isActive = role === r.id;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setRole(r.id)}
                            className={`relative p-4 rounded-xl border text-left transition-all ${isActive
                              ? "border-primary bg-primary/5 ring-1 ring-primary"
                              : "border-border hover:border-primary/30 hover:bg-muted/50"
                              }`}
                          >
                            <Icon className={`w-5 h-5 mb-3 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                            <div className={`font-semibold text-sm ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{r.label}</div>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <Form {...emailForm}>
                    <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
                      <FormField
                        control={emailForm.control}
                        name="email"
                        rules={{
                          required: "Email is required",
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <Label>Email</Label>
                            <FormControl>
                              <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input {...field} type="email" placeholder="you@example.com" className="pl-10 h-11" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        className="w-full h-11 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl font-semibold mt-2"
                        disabled={loading || !emailForm.formState.isValid}
                      >
                        {loading ? "Sending code..." : <span className="flex items-center gap-2">Continue <ArrowRight className="w-4 h-4" /></span>}
                      </Button>
                    </form>
                  </Form>
                  <p className="text-center text-xs text-muted-foreground mt-4">
                    By registering, you agree to the{" "}
                    <a href="#" className="text-primary hover:underline">Terms of Service</a> and{" "}
                    <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
                  </p>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <div className="mb-8">
                    <button onClick={() => setStep(0)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4 transition-colors">
                      &larr; Back
                    </button>
                    <h2 className="text-2xl font-bold mb-2">Check your email</h2>
                    <p className="text-muted-foreground">
                      We sent a 6-digit verification code to <span className="font-medium text-foreground">{emailForm.getValues("email")}</span>.
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 rounded-xl bg-destructive/10 text-destructive text-sm font-medium border border-destructive/20 flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-destructive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <AlertCircle className="w-3.5 h-3.5" />
                      </div>
                      {error}
                    </div>
                  )}

                  <Form {...otpForm}>
                    <form onSubmit={otpForm.handleSubmit(onOTPSubmit)} className="space-y-6">
                      <FormField
                        control={otpForm.control}
                        name="otp"
                        rules={{
                          required: "Verification code is required",
                          minLength: { value: 6, message: "Code must be 6 digits" },
                        }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col items-center">
                            <FormControl>
                              <InputOTP maxLength={6} autoFocus {...field}>
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={4} className="h-12 w-12 text-lg" />
                                  <InputOTPSlot index={5} className="h-12 w-12 text-lg" />
                                </InputOTPGroup>
                              </InputOTP>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button
                        type="submit"
                        className="w-full h-11 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl font-semibold"
                        disabled={loading || otpForm.watch("otp")?.length !== 6}
                      >
                        {loading ? "Verifying..." : "Verify Code"}
                      </Button>
                      <p className="text-center text-sm text-muted-foreground">
                        Didn't receive the code? <button type="button" className="text-primary hover:underline font-medium">Resend</button>
                      </p>
                    </form>
                  </Form>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="mb-4">
                    <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mb-3">
                      <ShieldCheck className="w-3.5 h-3.5" /> Provider Setup & Verification
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Facility Type & NPI Details</h2>
                    <p className="text-muted-foreground text-sm">
                      Enter your 10-digit National Provider Identifier (NPI) to auto-fill your credentials.
                    </p>
                  </div>

                  {npiFeedback && (
                    <div className={cn(
                      "p-4 rounded-xl text-sm font-medium border flex items-start gap-3 transition-all",
                      npiFeedback.type === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : npiFeedback.type === "error" ? "bg-destructive/10 border-destructive/20 text-destructive"
                          : "bg-blue-50 border-blue-200 text-blue-800"
                    )}>
                      {npiFeedback.type === "success" ? (
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-destructive" />
                      )}
                      <div className="space-y-2.5 w-full">
                        <p>{npiFeedback.msg}</p>
                        {npiFeedback.alreadyConnected && (
                          <div>
                            <Link href="/login">
                              <Button size="sm" className="bg-destructive hover:bg-destructive/90 text-white rounded-lg text-xs font-semibold px-4 h-9 shadow-sm">
                                Sign In to Existing Account &rarr;
                              </Button>
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 2: NPI Fields & Affiliated Name */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label htmlFor="ind-npi-input" className="text-sm font-semibold text-foreground">
                          Individual NPI (Type 1)
                        </Label>
                        <span className="text-[11px] text-muted-foreground font-medium">
                          10-digit Individual NPI
                        </span>
                      </div>
                      <Input
                        id="ind-npi-input"
                        type="text"
                        maxLength={10}
                        value={individualNpiInput}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          setIndividualNpiInput(val);
                          setNpiInput(val || groupNpiInput);
                        }}
                        placeholder="e.g. 1847291034"
                        className="h-11 font-mono tracking-wider text-sm rounded-xl"
                        autoFocus
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label htmlFor="grp-npi-input" className="text-sm font-semibold text-foreground">
                          Organizational / Group NPI (Type 2)
                        </Label>
                        <span className="text-[11px] text-muted-foreground font-medium">
                          10-digit Group NPI
                        </span>
                      </div>
                      <Input
                        id="grp-npi-input"
                        type="text"
                        maxLength={10}
                        value={groupNpiInput}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          setGroupNpiInput(val);
                          if (!individualNpiInput) setNpiInput(val);
                        }}
                        placeholder="e.g. 1982736450"
                        className="h-11 font-mono tracking-wider text-sm rounded-xl"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label htmlFor="aff-name-input" className="text-sm font-semibold text-foreground">
                          Affiliated Practice / Organization Name
                        </Label>
                        <span className="text-[11px] text-muted-foreground font-medium">
                          Optional
                        </span>
                      </div>
                      <Input
                        id="aff-name-input"
                        type="text"
                        value={affiliatedNameInput}
                        onChange={(e) => setAffiliatedNameInput(e.target.value)}
                        placeholder="e.g. Mount Sinai Medical Center / Desai Care Group"
                        className="h-11 text-sm rounded-xl"
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={() => handleNpiLookup()}
                      className="w-full h-11 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white border-0 rounded-xl font-semibold flex items-center justify-center gap-2"
                      disabled={npiLoading || (!individualNpiInput && !groupNpiInput && !npiInput)}
                    >
                      {npiLoading ? "Searching Database..." : <span className="flex items-center gap-2">Fetch Details & Auto-Fill <ArrowRight className="w-4 h-4" /></span>}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>

          </AnimatePresence>

        </div>
      </div>
    </div>
  );
}
